import { clsx, type ClassValue } from 'clsx';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import type { Tone } from '../types';

/** Same helper both apps keep in `lib/utils`. Duplicated rather than imported —
 * a package can't reach into an app. */
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

/**
 * The small marks the client page is built from.
 *
 * ── A NOTE ON THE THIRD COPY ──
 *
 * `@codezo/quote-detail` and `@codezo/brand-detail` each carry their own copy
 * of Card/SectionHead/Badge/EmptyState, and brand's says plainly: "if a third
 * page ever needs them the answer is to lift them into their own package, not
 * to make brand depend on quote." This is that third page.
 *
 * It is still a copy, deliberately and temporarily: lifting them now means
 * editing two shipping v2 pages in the same change that introduces a third, and
 * this one is meant to land without disturbing either. The extraction is the
 * next piece of work, not a someday — see the package README.
 *
 * No Button lives here, for the reason the other two state: every action on
 * this page is a ReactNode slot the app fills, because the app owns
 * permissions, loading state, and what each control opens.
 */

/* ────────────────────────── surfaces ────────────────────────── */

export function Card({
	children,
	className,
	padded,
}: {
	children: ReactNode;
	/** Adds the standard gutter. Cards that own their own rows pass nothing. */
	padded?: boolean;
	className?: string;
}) {
	return (
		<div
			className={cn(
				'overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface',
				padded && 'p-5',
				className
			)}
		>
			{children}
		</div>
	);
}

/** The uppercase label used for column heads and small captions throughout. */
export function MicroLabel({ children, className }: { children: ReactNode; className?: string }) {
	return (
		<span
			className={cn(
				'text-2xs font-medium uppercase tracking-[0.08em] text-text-4',
				className
			)}
		>
			{children}
		</span>
	);
}

/** A section's heading row: title, an optional count/hint, and the app's own
 * action on the right. */
export function SectionHead({
	title,
	meta,
	action,
}: {
	title: string;
	meta?: ReactNode;
	action?: ReactNode;
}) {
	return (
		<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
			<div className="flex min-w-0 items-baseline gap-3">
				<h2 className="text-base font-medium text-text-1">{title}</h2>
				{meta && <span className="truncate text-xs text-text-4">{meta}</span>}
			</div>
			{action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
		</div>
	);
}

/* ────────────────────────── marks ────────────────────────── */

const TONE_CLASS: Record<Tone, string> = {
	neutral: 'bg-status-neutral-soft text-text-2',
	info: 'bg-status-info-soft text-status-info',
	success: 'bg-status-success-soft text-status-success',
	warn: 'bg-status-warn-soft text-status-warn',
	danger: 'bg-status-danger-soft text-status-danger',
	accent: 'bg-accent-soft text-accent',
};

export function Badge({
	tone = 'neutral',
	children,
	dot,
	className,
}: {
	tone?: Tone;
	children: ReactNode;
	/** The leading status dot. Off for badges that are labels, not states. */
	dot?: boolean;
	className?: string;
}) {
	return (
		<span
			className={cn(
				'inline-flex items-center gap-1.5 rounded-[var(--r-1)] px-1.5 py-0.5 text-2xs font-medium whitespace-nowrap',
				TONE_CLASS[tone],
				className
			)}
		>
			{dot && <span className="h-1 w-1 rounded-full bg-current" />}
			{children}
		</span>
	);
}

/**
 * Initials on a tinted tile — the stand-in wherever a person or an account has
 * no image. Deriving the initials here rather than taking them as a prop keeps
 * every caller from writing the same two-word split.
 */
export function Avatar({
	name,
	size = 30,
	className,
}: {
	name?: string | null;
	size?: number;
	className?: string;
}) {
	const initials = (name ?? '')
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? '')
		.join('');

	return (
		<span
			className={cn(
				'inline-flex shrink-0 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill font-mono text-text-2',
				className
			)}
			style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
		>
			{initials || '—'}
		</span>
	);
}

/**
 * A palette as one bar of proportional stripes.
 *
 * Used on the brand cards, and it takes the WHOLE palette for the reason the
 * brand hero settled on: a bar showing two of five colors is a bar that had to
 * pick, and every rule for picking was wrong on some real kit.
 */
export function PaletteBar({
	colors,
	className,
}: {
	colors?: readonly string[] | null;
	className?: string;
}) {
	const stops = colors && colors.length > 0 ? colors : null;

	return (
		<div className={cn('flex h-7 w-full', className)}>
			{stops ? (
				stops.map((hex, i) => (
					<span
						key={`${hex}-${i}`}
						title={hex.toUpperCase()}
						className="h-full flex-1"
						style={{ background: hex }}
					/>
				))
			) : (
				/* No palette is a real state, not a gap — the stripes read as
				   "nothing set" rather than as a band that failed to paint. */
				<span className="bg-stripes h-full flex-1 border-b border-line-1" />
			)}
		</div>
	);
}

/** The centred "nothing here yet" panel a section shows in place of its list. */
export function EmptyState({
	title,
	hint,
	action,
}: {
	title: string;
	hint?: string;
	action?: ReactNode;
}) {
	return (
		<Card className="py-12">
			<div className="flex flex-col items-center gap-3 text-center">
				<div className="text-sm text-text-2">{title}</div>
				{hint && <div className="max-w-xs text-xs text-text-4">{hint}</div>}
				{action && <div className="mt-1">{action}</div>}
			</div>
		</Card>
	);
}
